// Logic, API transform helpers, and small shared components for the
// Dr. SecDim mentor dropdown.
//
// Backend contract:
// `/learn/learning-path` GET returns a window of nodes (1 before the current node +
// current + 3 after) and POST triggers regeneration; `/learn/user-customisation`
// GET/POST/PATCH stores the preferences below. Type interfaces live in
// frontend/src/types.d.ts.

import Link from '@mui/material/Link'
import { createUrl } from '../../shared/UrlUtils/urlUtils'
import type { PanelMode } from './DrSecDimPanel'

export const MAX_PATH_NODES = 5

/**
 * Tooltip content shown on the regenerate control when the user is blocked
 * by subscription. "Subscribe" links out to the subscription page instead
 * of leaving the tooltip purely informational.
 */
export function RegenerateSubscribeTooltip() {
	return (
		<>
			<Link
				href={createUrl(import.meta.env.VITE_SUBSCRIPTION_PAGE_LINK, {
					params: { origin: 'learn' },
				})}
				color='inherit'
				underline='always'
				onClick={() => sessionStorage.setItem('redirectURI', window.location.href)}
			>
				Subscribe
			</Link>
			{' to regenerate learning path'}
		</>
	)
}

/**
 * Determine whether the Dr. SecDim panel should render in compact mode.
 * The panel shrinks to fit the "ready" card and the generation-progress
 * screen instead of reserving the wizard's full height.
 */
export function compactPanel(
	mode: PanelMode,
	viewState: MentorViewState,
	isGenerating: boolean,
	mentor: MentorSnapshot | null,
): boolean {
	return (
		mode === 'view' &&
		(isGenerating || (viewState === 'ready' && Boolean(mentor)))
	)
}

export function getCurrentNode(nodes: PathNode[]): PathNode | undefined {
	return nodes.find(node => node.status === 'current')
}

export function isAchievementNode(node: PathNode): boolean {
	return node.kind === 'achievement'
}

export function getPreviewStartIndex(nodes: PathNode[]): number {
	if (nodes.length === 0) return 0
	const currentIndex = nodes.findIndex(node => node.status === 'current')
	if (currentIndex < 0) return 0
	return Math.max(0, currentIndex - 1)
}

export function selectPathPreview(nodes: PathNode[]): PathNode[] {
	if (nodes.length === 0) return []
	const start = getPreviewStartIndex(nodes)
	return nodes.slice(start, start + MAX_PATH_NODES)
}

export function hasScoreOrRank(
	snapshot: Pick<MentorSnapshot, 'score' | 'rank'>,
): boolean {
	return snapshot.score !== undefined || Boolean(snapshot.rank)
}

export function hasCustomisation(
	customisation: UserCustomisation | null | undefined,
): boolean {
	if (!customisation) return false
	return (
		Boolean(customisation.language?.length) ||
		Boolean(customisation.role) ||
		Boolean(customisation.seniority) ||
		Boolean(customisation.objective) ||
		Boolean(customisation.timeAvailable)
	)
}

const KIND_MAP: Record<string, PathNodeKind> = {
	Challenge: 'play',
	Topic: 'learn',
	Course: 'learn',
}

// Maps the frontend's free-text commitment options to their ISO-8601 duration
// and Django timedelta representations. Add new options here; both lookup maps
// are derived automatically below.
const COMMITMENTS = [
	{ label: '10 minutes a day', iso8601: 'PT10M', timedelta: '00:10:00' },
	{ label: '30 minutes a day', iso8601: 'PT30M', timedelta: '00:30:00' },
	{ label: '1 hour a week', iso8601: 'PT1H', timedelta: '01:00:00' },
	{ label: '2-3 hours a week', iso8601: 'PT2H30M', timedelta: '02:30:00' },
	{ label: 'Intensive mode', iso8601: 'PT4H', timedelta: '04:00:00' },
] as const

const LABEL_TO_DURATION: Record<string, string> = Object.fromEntries(
	COMMITMENTS.map(c => [c.label, c.iso8601]),
)

const DURATION_TO_LABEL: Record<string, string> = Object.fromEntries(
	COMMITMENTS.map(c => [c.timedelta, c.label]),
)

export function commitmentToDuration(timeAvailable?: string): string | null {
	if (!timeAvailable) return null
	return LABEL_TO_DURATION[timeAvailable] ?? null
}

function durationToCommitment(duration?: string | null): string | undefined {
	if (!duration) return undefined
	// Django may serialize timedelta as "HH:MM:SS.uuuuuu".
	const normalised = duration.replace(/\.\d+$/, '')
	return DURATION_TO_LABEL[normalised] ?? undefined
}

/** Convert a backend UserCustomisation to the frontend shape. */
export function fromBackendCustomisation(
	data: BackendUserCustomisation,
): UserCustomisation {
	return {
		language: data.language ?? [],
		role: data.role ?? undefined,
		seniority: data.seniority ?? undefined,
		objective: data.objective ?? undefined,
		timeAvailable: durationToCommitment(data.time_available),
	}
}

/** Convert the frontend questionnaire form to the backend payload shape. */
export function toBackendCustomisation(
	form: UserCustomisation,
): UserCustomisationPayload {
	return {
		language: form.language ?? [],
		role: form.role ?? null,
		seniority: form.seniority ?? null,
		objective: form.objective ?? null,
		time_available: commitmentToDuration(form.timeAvailable),
	}
}

/**
 * Convert an array of backend learning-path nodes into the frontend PathNode[].
 *
 * The backend window is ordered: [NODES_BEFORE completed node(s), current node,
 * NODES_AFTER upcoming node(s)]. The first node with a null `completed_at` is
 * treated as "current"; everything before it is "completed" and everything after
 * is "upcoming". Resource titles are resolved from Learn or Play before this
 * transform runs.
 */
/**
 * Build a navigation URL from the node's type and resolved slugs.
 *
 * Slugs are resolved by the useLearningPath hook which enriches each node
 * from the owning Course / Topic / Challenge resource before this transform
 * runs. If slugs are unavailable (e.g. a deleted resource), href is left
 * undefined so the CTA can fall back gracefully.
 */
function buildNodeHref(
	type: string,
	slug?: string,
	courseSlug?: string | null,
): string | undefined {
	switch (type) {
		case 'Course':
			return slug ? `/course/${slug}` : undefined
		case 'Topic':
			return slug && courseSlug ? `/course/${courseSlug}/topic/${slug}` : undefined
		case 'Challenge':
			// Challenge URLs require game-slug resolution — handled
			// asynchronously via useChallengeUrl in DrSecDim.
			return undefined
		default:
			return undefined
	}
}

export function fromBackendNodes(nodes: BackendLearnPathNode[]): PathNode[] {
	if (nodes.length === 0) return []

	// Locate the current node: first with a null completed_at.
	let currentIndex = nodes.findIndex(n => n.completed_at === null)
	if (currentIndex < 0) {
		// All nodes are completed; treat the last as current.
		currentIndex = nodes.length - 1
	}

	return nodes.map((n, i): PathNode => {
		let status: PathNodeStatus
		if (i < currentIndex) {
			status = 'completed'
		} else if (i === currentIndex) {
			status = 'current'
		} else {
			status = 'upcoming'
		}

		const kind = KIND_MAP[n.type] ?? 'learn'

		return {
			id: String(n.id),
			typeUuid: n.type_uuid,
			kind,
			title: n.title ?? `${n.type} node`,
			status,
			justification: n.justification ?? undefined,
			href: buildNodeHref(n.type, n.slug, n.course_slug),
		}
	})
}

// Recommendation CTAs will eventually be generator/backend-supplied, so the URL
// must be validated before navigation: only http(s) links to the app's own origin
// or the Play domain are allowed, blocking javascript: URLs and open redirects.
function getPlayFrontendOrigin(): string {
	try {
		return new URL(import.meta.env.VITE_SECDIM_PLAY_FRONTEND!).origin
	} catch {
		return 'https://play.secdim.com'
	}
}

function getAllowedCtaOrigins(playOrigin: string): string[] {
	const origins = ['https://play.secdim.com']
	if (playOrigin && !origins.includes(playOrigin)) {
		origins.push(playOrigin)
	}
	return origins
}

export function safeCtaHref(
	href: string,
	currentOrigin: string,
): string | null {
	let url: URL
	try {
		url = new URL(href, currentOrigin)
	} catch {
		return null
	}
	if (url.protocol !== 'http:' && url.protocol !== 'https:') return null

	const allowed = getAllowedCtaOrigins(getPlayFrontendOrigin())
	if (url.origin !== currentOrigin && !allowed.includes(url.origin)) return null
	return url.toString()
}

export function resolveMentorViewState({
	isLoading,
	isError,
	snapshot,
}: MentorQueryResult): MentorViewState {
	if (isLoading) return 'loading'
	if (isError) return 'error'
	if (!snapshot) return 'empty'
	return 'ready'
}
